import { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { useTranslation } from 'react-i18next';
import EditOfficialDialog from "./EditOfficialDialog";
import CreateOfficialDialog from "./CreateOfficialDialog";
import ImportOfficialsDialog from "./ImportOfficialsDialog";
import OfficialCard from "./OfficialCard";
import OfficialsTable from "./OfficialsTable";
import { LoadingOverlay } from "./LoadingOverlay";
import { 
  Users, 
  UserPlus, 
  FileText, 
  CheckCircle, 
  Clock, 
  ArrowRight,
  Filter,
  Building2,
  MapPin,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  Upload
} from "lucide-react";

// Revenue Department designations (for filter - excludes Tahsildar as they are super authority)
const REVENUE_DESIGNATIONS_FILTER = [
  { value: "tahsildar", labelKey: "designation.tahsildar" },
  { value: "deputy_tahsildar", labelKey: "designation.deputy_tahsildar" },
  { value: "shirestedar", labelKey: "designation.shirestedar" },
  { value: "revenue_inspector", labelKey: "designation.revenue_inspector" },
  { value: "village_accountant", labelKey: "designation.village_accountant" },
  { value: "first_division_clerk", labelKey: "designation.first_division_clerk" },
  { value: "second_division_clerk", labelKey: "designation.second_division_clerk" },
];

// Revenue Department designations (full list for create official)
const REVENUE_DESIGNATIONS = [
  { value: "tahsildar", labelKey: "designation.tahsildar" },
  { value: "deputy_tahsildar", labelKey: "designation.deputy_tahsildar" },
  { value: "shirestedar", labelKey: "designation.shirestedar" },
  { value: "revenue_inspector", labelKey: "designation.revenue_inspector" },
  { value: "village_accountant", labelKey: "designation.village_accountant" },
  { value: "first_division_clerk", labelKey: "designation.first_division_clerk" },
  { value: "second_division_clerk", labelKey: "designation.second_division_clerk" },
];

// Survey Department designations (for filter - excludes DDLR as they are super authority)
const SURVEY_DESIGNATIONS_FILTER = [
  { value: "adlr", labelKey: "designation.adlr" },
  { value: "surveyor", labelKey: "designation.surveyor" },
  { value: "case_worker", labelKey: "designation.case_worker" },
  { value: "superintendent", labelKey: "designation.superintendent" },
  { value: "survey_supervisor", labelKey: "designation.survey_supervisor" },
];

// Survey Department designations (full list for create official)
const SURVEY_DESIGNATIONS = [
  { value: "ddlr", labelKey: "designation.ddlr" },
  { value: "adlr", labelKey: "designation.adlr" },
  { value: "surveyor", labelKey: "designation.surveyor" },
  { value: "case_worker", labelKey: "designation.case_worker" },
  { value: "superintendent", labelKey: "designation.superintendent" },
  { value: "survey_supervisor", labelKey: "designation.survey_supervisor" },
];

// Designations allowed for bulk assignment
const BULK_ASSIGN_DESIGNATIONS = [
  { value: "deputy_tahsildar", labelKey: "designation.deputy_tahsildar" },
  { value: "shirestedar", labelKey: "designation.shirestedar" },
  { value: "revenue_inspector", labelKey: "designation.revenue_inspector" },
  { value: "village_accountant", labelKey: "designation.village_accountant" },
  { value: "first_division_clerk", labelKey: "designation.first_division_clerk" },
  { value: "second_division_clerk", labelKey: "designation.second_division_clerk" },
];

// Hobli options
const HOBLIS = [
  { value: "all", labelKey: "hobli.all" },
  { value: "Kasaba", labelKey: "hobli.kasaba" },
  { value: "Kudur", labelKey: "hobli.kudur" },
  { value: "Madabalu", labelKey: "hobli.madabalu" },
  { value: "Tippasandra", labelKey: "hobli.tippasandra" },
];

// Circle options by hobli
const CIRCLES_BY_HOBLI = {
  "Kasaba": [
    { value: "bachenahatti", label: "Bachenahatti", label_kn: "ಬಾಚೇನಹಟ್ಟಿ" },
    { value: "kalya", label: "Kalya", label_kn: "ಕಲ್ಯಾ" },
    { value: "shreepathi_halli", label: "ShreePathi Halli", label_kn: "ಶ್ರೀಪತಿಹಳ್ಳಿ" },
    { value: "kalari_kaval", label: "Kalari Kaval", label_kn: "ಕಾಳಾರಿಕಾವಲ್" },
    { value: "harthi", label: "Harthi", label_kn: "ಹರ್ತಿ" },
    { value: "konda_halli", label: "Konda Halli", label_kn: "ಕೋಂಡಹಳ್ಳಿ" },
    { value: "vishwanathapura", label: "Vishwanathapura", label_kn: "ವಿಶ್ವನಾಥಪುರ" },
    { value: "mayanayakana_halli", label: "Mayanayakana Halli", label_kn: "ಮಾಯನಾಯ್ಕನಹಳ್ಳಿ" },
    { value: "kalluru", label: "Kalluru", label_kn: "ಕಲ್ಲೂರು" },
    { value: "agalakote", label: "Agalakote", label_kn: "ಆಗಲಕೋಟೆ" },
    { value: "taggikuppe", label: "Taggikuppe", label_kn: "ತಗ್ಗೀಕುಪ್ಪೆ" },
    { value: "dubbagottige", label: "Dubbagottige", label_kn: "ದುಬ್ಬಗೊಟ್ಟಿಗೆ" },
    { value: "nethana_halli", label: "Nethana Halli", label_kn: "ನೇತೇನಹಳ್ಳಿ" },
    { value: "sathnur", label: "Sathnur", label_kn: "ಸಾತನೂರು" },
    { value: "bylada_kere", label: "Bylada Kere", label_kn: "ಬ್ಯಾಲದಕೆರೆ" },
  ],
  "Kudur": [
    { value: "maruru", label: "Maruru", label_kn: "ಮರೂರು" },
    { value: "ajjahalli", label: "Ajjahalli", label_kn: "ಅಜ್ಜಹಳ್ಳಿ" },
    { value: "hulikal", label: "Hulikal", label_kn: "ಹುಲಿಕಲ್ಲು" },
    { value: "biskuru", label: "Biskuru", label_kn: "ಬಿಸ್ಕೂರು" },
    { value: "kananuru", label: "Kananuru", label_kn: "ಕಣನೂರು" },
    { value: "marasandra", label: "Marasandra", label_kn: "ಮಾರಸಂದ್ರ" },
    { value: "manniganahalli", label: "Manniganahalli", label_kn: "ಮಣ್ಣಿಗನಹಳ್ಳಿ" },
    { value: "adarangi", label: "Adarangi", label_kn: "ಅದರಂಗಿ" },
    { value: "kuduru", label: "Kuduru", label_kn: "ಕುದೂರು" },
    { value: "madigondanahalli", label: "Madigondanahalli", label_kn: "ಮಾದಿಗೊಂಡನಹಳ್ಳಿ" },
    { value: "bettahalli", label: "Bettahalli", label_kn: "ಬೆಟ್ಟಹಳ್ಳಿ" },
    { value: "gollahalli", label: "Gollahalli", label_kn: "ಗೊಲ್ಲಹಳ್ಳಿ" },
    { value: "narasandra", label: "Narasandra", label_kn: "ನಾರಸಂದ್ರ" },
  ],
  "Madabalu": [
    { value: "seegekuppe", label: "Seegekuppe", label_kn: "ಸೀಗೆಕುಪ್ಪೆ" },
    { value: "madabal", label: "Madabal", label_kn: "ಮಾಡಬಾಳು" },
    { value: "dabbaguli", label: "Dabbaguli", label_kn: "ದಬ್ಬಗುಳಿ" },
    { value: "mattikere", label: "Mattikere", label_kn: "ಮಟ್ಟಿಕೆರೆ" },
    { value: "karalamangala", label: "Karalamangala", label_kn: "ಕರಳಮಂಗಲ" },
    { value: "ajjanahalli", label: "Ajjanahalli", label_kn: "ಅಜ್ಜನಹಳ್ಳಿ" },
    { value: "gajjagaraguppe", label: "Gajjagaraguppe", label_kn: "ಗಜ್ಜಗರಗುಪ್ಪೆ" },
    { value: "hanchikuppe", label: "Hanchikuppe", label_kn: "ಹಂಚಿಕುಪ್ಪೆ" },
    { value: "hulikatte", label: "Hulikatte", label_kn: "ಹುಲಿಕಟ್ಟೆ" },
    { value: "chakrabhavi", label: "Chakrabhavi", label_kn: "ಚಕ್ರಭಾವಿ" },
    { value: "belagavadi", label: "Belagavadi", label_kn: "ಬೆಳಗಾವಡಿ" },
  ],
  "Tippasandra": [
    { value: "hullena_halli", label: "Hullena Halli", label_kn: "ಹುಳ್ಳೇನಹಳ್ಳಿ" },
    { value: "hebbalalu", label: "Hebbalalu", label_kn: "ಹೆಬ್ಬಳಲು" },
    { value: "baginagere", label: "Baginagere", label_kn: "ಬಗಿನಗೆರೆ" },
    { value: "sanki_ghatta", label: "Sanki Ghatta", label_kn: "ಸಂಕೀಘಟ್ಟ" },
    { value: "nerelekere", label: "Nerelekere", label_kn: "ನೇರಳೆಕೆರೆ" },
    { value: "tippasandra", label: "Tippasandra", label_kn: "ತಿಪ್ಪಸಂದ್ರ" },
    { value: "dodda_somanahalli", label: "Dodda Somanahalli", label_kn: "ದೊಡ್ಡಸೋಮನಹಳ್ಳಿ" },
    { value: "mallasandra", label: "Mallasandra", label_kn: "ಮಲ್ಲಸಂದ್ರ" },
    { value: "chikka_mudigere", label: "Chikka Mudigere", label_kn: "ಚಿಕ್ಕಮುದಿಗೆರೆ" },
    { value: "mayasandra", label: "Mayasandra", label_kn: "ಮಾಯಸಂದ್ರ" },
    { value: "chikkahalli", label: "Chikkahalli", label_kn: "ಚಿಕ್ಕಹಳ್ಳಿ" },
  ],
};

// Helper function to get circle label based on language
const getCircleLabel = (circle, language) => {
  if (language === 'kn' && circle.label_kn) {
    return circle.label_kn;
  }
  return circle.label;
};

const ALL_DESIGNATIONS = [...REVENUE_DESIGNATIONS, ...SURVEY_DESIGNATIONS];

const DEPARTMENTS = [
  { value: "revenue", labelKey: "department.revenue" },
  { value: "survey", labelKey: "department.survey" },
];

// Map designation to department
const getDesignationDepartment = (designation) => {
  const revenueDesignations = ["tahsildar", "deputy_tahsildar", "shirestedar", "revenue_inspector", "village_accountant", "first_division_clerk", "second_division_clerk"];
  return revenueDesignations.includes(designation) ? "revenue" : "survey";
};

// Check if user is a revenue super authority (Tahsildar)
const isRevenueSuperAuthority = (designation) => designation === "tahsildar";

// Check if user is a survey super authority (DDLR/ADLR)
const isSurveySuperAuthority = (designation) => ["ddlr", "adlr"].includes(designation);

// VA Summary Card Component - Shows count of Village Accountants per Hobli
const VAHobliSummaryCard = memo(({ officials, t, onHobliClick }) => {
  // Mapping from Kannada to English hobli names
  const kannadaToEnglish = {
    "ಕಸಬಾ": "Kasaba",
    "ಕುದೂರು": "Kudur",
    "ಮಾಡಬಾಳು": "Madabalu",
    "ತಿಪ್ಪಸಂದ್ರ": "Tippasandra",
    "ಮಂಚನಬೆಲೆ": "Manchanabele",
  };

  // Calculate VA count per Hobli from officials' circle_assignments
  const hobliCounts = useMemo(() => {
    const counts = {
      Kasaba: 0,
      Kudur: 0,
      Madabalu: 0,
      Tippasandra: 0,
    };
    
    officials.forEach(official => {
      if (official.designation === "village_accountant" && official.circle_assignments?.length > 0) {
        // Get unique hoblis for this VA - handle both object and string formats
        const uniqueHoblis = [...new Set(official.circle_assignments.map(a => {
          if (typeof a === 'string') return a;
          return a?.hobli || '';
        }).filter(Boolean))];
        uniqueHoblis.forEach(hobli => {
          // Translate Kannada to English if needed
          const englishHobli = kannadaToEnglish[hobli] || hobli;
          if (counts.hasOwnProperty(englishHobli)) {
            counts[englishHobli]++;
          } else if (counts.hasOwnProperty(hobli)) {
            // Fallback: try original name
            counts[hobli]++;
          }
        });
      }
    });
    
    return counts;
  }, [officials]);

  const totalVAs = officials.filter(o => o.designation === "village_accountant").length;
  
  return (
    <Card className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          {t('officialsManagement.vaDistribution') || 'Village Accountants Distribution'}
        </h3>
        <Badge variant="outline" className="bg-white text-amber-700 border-amber-300">
          {t('officialsManagement.total') || 'Total'}: {totalVAs}
        </Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(hobliCounts).map(([hobli, count]) => (
          <button
            key={hobli}
            onClick={() => onHobliClick(hobli)}
            className="flex flex-col items-center p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer"
          >
            <span className="text-2xl font-bold text-amber-600">{count}</span>
            <span className="text-xs text-slate-600 mt-1">{t(`hobli.${hobli.toLowerCase()}`) || hobli}</span>
          </button>
        ))}
      </div>
    </Card>
  );
});

VAHobliSummaryCard.displayName = 'VAHobliSummaryCard';

export default function OfficialsManagement({ user }) {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'kn';
  const [officials, setOfficials] = useState([]);
  const [allOfficials, setAllOfficials] = useState([]); // Store all officials for counting
  const [assignableOfficials, setAssignableOfficials] = useState([]);
  const [unassignedApps, setUnassignedApps] = useState([]);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [unassignedPages, setUnassignedPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterDesignation, setFilterDesignation] = useState("");
  const [assignFilterDepartment, setAssignFilterDepartment] = useState("");
  
  // Determine user's department based on designation
  const userDesignation = user?.designation || "";
  const isRevenueAuthority = isRevenueSuperAuthority(userDesignation);
  const isSurveyAuthority = isSurveySuperAuthority(userDesignation);
  
  // Department filter - locked based on user's authority
  const lockedDepartment = isRevenueAuthority ? "revenue" : (isSurveyAuthority ? "survey" : "");
  const [filterDepartment, setFilterDepartment] = useState(lockedDepartment);
  const isDepartmentLocked = isRevenueAuthority || isSurveyAuthority;
  
  // Get designations to show in filter based on user's authority
  const getFilterDesignations = () => {
    if (isRevenueAuthority) {
      return REVENUE_DESIGNATIONS_FILTER;
    } else if (isSurveyAuthority) {
      return SURVEY_DESIGNATIONS_FILTER;
    } else {
      // For other users, show based on selected department
      if (filterDepartment === "revenue") return REVENUE_DESIGNATIONS_FILTER;
      if (filterDepartment === "survey") return SURVEY_DESIGNATIONS_FILTER;
      return [...REVENUE_DESIGNATIONS_FILTER, ...SURVEY_DESIGNATIONS_FILTER];
    }
  };
  
  // Location filters for unassigned applications
  const [unassignedHobliFilter, setUnassignedHobliFilter] = useState("all");
  const [unassignedCircleFilter, setUnassignedCircleFilter] = useState("all");
  const [unassignedPage, setUnassignedPage] = useState(1);
  const [unassignedLimit] = useState(20);
  
  // Location filters for officials tab (for filtering VAs by circle)
  const [filterHobli, setFilterHobli] = useState("all");
  const [filterCircle, setFilterCircle] = useState("all");
  const [circleVillagesData, setCircleVillagesData] = useState([]); // Data from circle_villages API
  
  // Pagination for officials
  const [officialsPage, setOfficialsPage] = useState(1);
  const [officialsPerPage] = useState(25);
  
  // Bulk assign states
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [bulkAssignOfficial, setBulkAssignOfficial] = useState("");
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  
  // Get available circles based on selected hobli
  const availableCircles = useMemo(() => {
    if (unassignedHobliFilter === "all") return [];
    return CIRCLES_BY_HOBLI[unassignedHobliFilter] || [];
  }, [unassignedHobliFilter]);
  
  // Get available circles for officials filter based on filterHobli
  // Merges hardcoded circles with API data to ensure all circles are shown
  const filterAvailableCircles = useMemo(() => {
    if (filterHobli === "all") return [];
    // Get hardcoded circles as the base (guaranteed to have all circles)
    const hardcodedCircles = CIRCLES_BY_HOBLI[filterHobli] || [];
    
    // Get API data for village counts
    const apiCirclesMap = new Map();
    circleVillagesData
      .filter(cv => cv.hobli?.toLowerCase() === filterHobli.toLowerCase())
      .forEach(cv => {
        const key = (cv.circle_en || cv.circle || '').toLowerCase();
        apiCirclesMap.set(key, {
          villageCount: cv.villages?.length || 0,
          circle_kn: cv.circle_kn || cv.circle
        });
      });
    
    // Merge hardcoded circles with API village counts
    return hardcodedCircles.map(hc => {
      const apiData = apiCirclesMap.get(hc.value.toLowerCase());
      return {
        value: hc.value,
        label: hc.label,
        label_kn: hc.label_kn,
        villageCount: apiData?.villageCount || 0
      };
    });
  }, [filterHobli, circleVillagesData]);
  
  // Get the current super authority's department (locked for create/edit)
  const userDepartment = user?.department || getDesignationDepartment(user?.designation) || "";
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCircleAssignDialog, setShowCircleAssignDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  
  // Selected items
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedOfficial, setSelectedOfficial] = useState("");
  const [officialToEdit, setOfficialToEdit] = useState(null);
  const [officialToDelete, setOfficialToDelete] = useState(null);
  const [officialForCircle, setOfficialForCircle] = useState(null);
  const [circleToRemove, setCircleToRemove] = useState(null);
  
  // Circle assignment form states
  const [circleAssignHobli, setCircleAssignHobli] = useState("");
  const [circleAssignCircle, setCircleAssignCircle] = useState("");
  const [circleAssignLoading, setCircleAssignLoading] = useState(false);
  const [showRemoveCircleDialog, setShowRemoveCircleDialog] = useState(false);

  useEffect(() => {
    fetchOfficials();
    fetchAssignableOfficials();
  }, [filterDesignation, filterDepartment, filterHobli, filterCircle]);

  // Fetch all officials for counts on initial load only
  useEffect(() => {
    fetchAllOfficialsForCounts();
    fetchCircleVillagesData();
  }, []);

  // Separate useEffect for unassigned applications with filter dependencies
  useEffect(() => {
    fetchUnassignedApplications();
  }, [unassignedHobliFilter, unassignedCircleFilter, unassignedPage]);

  // Fetch circle_villages data from API for enhanced filtering
  const fetchCircleVillagesData = async () => {
    try {
      const response = await api.get("/circle-villages");
      if (response.data?.data) {
        setCircleVillagesData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching circle-villages data:", error);
      // Fall back to hardcoded data
    }
  };

  const fetchOfficials = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterDesignation) params.append("designation", filterDesignation);
      if (filterDepartment) params.append("department", filterDepartment);
      if (filterHobli && filterHobli !== "all") params.append("hobli", filterHobli);
      if (filterCircle && filterCircle !== "all") params.append("circle", filterCircle);
      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await api.get(`/officials${queryString}`);
      setOfficials(response.data);
    } catch (error) {
      console.error("Error fetching officials:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all officials for counting (without filters)
  const fetchAllOfficialsForCounts = async () => {
    try {
      const response = await api.get("/officials");
      setAllOfficials(response.data);
    } catch (error) {
      console.error("Error fetching all officials:", error);
    }
  };

  // Refresh both filtered and all officials lists
  const refreshOfficials = async () => {
    await Promise.all([fetchOfficials(), fetchAllOfficialsForCounts()]);
  };

  const fetchAssignableOfficials = async () => {
    try {
      const response = await api.get("/officials/assignable");
      setAssignableOfficials(response.data);
    } catch (error) {
      console.error("Error fetching assignable officials:", error);
    }
  };

  const fetchUnassignedApplications = async () => {
    try {
      const params = new URLSearchParams();
      params.append("page", unassignedPage);
      params.append("limit", unassignedLimit);
      if (unassignedHobliFilter && unassignedHobliFilter !== "all") {
        params.append("hobli", unassignedHobliFilter);
      }
      if (unassignedCircleFilter && unassignedCircleFilter !== "all") {
        params.append("circle", unassignedCircleFilter);
      }
      const response = await api.get(`/officials/unassigned-applications?${params.toString()}`);
      setUnassignedApps(response.data.applications || []);
      setUnassignedTotal(response.data.total || 0);
      setUnassignedPages(response.data.pages || 1);
    } catch (error) {
      console.error("Error fetching unassigned applications:", error);
    }
  };

  // Handle hobli filter change - reset circle and page
  const handleHobliFilterChange = (value) => {
    setUnassignedHobliFilter(value);
    setUnassignedCircleFilter("all");
    setUnassignedPage(1);
  };

  // Handle circle filter change - reset page
  const handleCircleFilterChange = (value) => {
    setUnassignedCircleFilter(value);
    setUnassignedPage(1);
  };

  // Bulk assign handler
  const handleBulkAssign = async () => {
    if (!unassignedCircleFilter || unassignedCircleFilter === "all" || !bulkAssignOfficial) {
      toast.error(t('officialsManagement.selectCircleAndOfficial'));
      return;
    }
    
    setBulkAssignLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("circle", unassignedCircleFilter);
      params.append("official_id", bulkAssignOfficial);
      if (unassignedHobliFilter && unassignedHobliFilter !== "all") {
        params.append("hobli", unassignedHobliFilter);
      }
      
      const response = await api.post(`/officials/bulk-assign-by-circle?${params.toString()}`);
      toast.success(t('officialsManagement.bulkAssignSuccess', { count: response.data.assigned_count, name: response.data.official_name }));
      setShowBulkAssignDialog(false);
      setBulkAssignOfficial("");
      fetchUnassignedApplications();
      fetchOfficials();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('officialsManagement.bulkAssignError'));
    } finally {
      setBulkAssignLoading(false);
    }
  };

  // Get officials eligible for bulk assignment
  const bulkAssignableOfficials = useMemo(() => {
    return assignableOfficials.filter(o => 
      BULK_ASSIGN_DESIGNATIONS.some(d => d.value === o.designation)
    );
  }, [assignableOfficials]);

  const handleCreateOfficial = useCallback(async (formData) => {
    await api.post("/officials", formData);
    toast.success(t('officialsManagement.officialCreated'));
    // Refresh all officials lists including counts
    await refreshOfficials();
    fetchAssignableOfficials();
  }, [t]);

  const handleToggleStatus = async (official) => {
    try {
      const response = await api.put(`/officials/${official.id}/toggle-status`);
      const statusKey = response.data.is_disabled ? 'officialsManagement.officialDisabled' : 'officialsManagement.officialEnabled';
      toast.success(t(statusKey));
      fetchOfficials();
      fetchAssignableOfficials();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('officialsManagement.errorToggling'));
    }
  };

  const handleDeleteOfficial = async () => {
    if (!officialToDelete) return;
    
    try {
      const response = await api.delete(`/officials/${officialToDelete.id}`);
      toast.success(t('officialsManagement.officialDeleted', { count: response.data.reassigned_count }));
      setShowDeleteDialog(false);
      setOfficialToDelete(null);
      // Refresh all officials lists including counts
      await refreshOfficials();
      fetchAssignableOfficials();
      fetchUnassignedApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('officialsManagement.errorDeleting'));
    }
  };

  const handleAssignApplication = async () => {
    if (!selectedApplication || !selectedOfficial) return;
    
    try {
      await api.put(`/applications/${selectedApplication.id}/assign`, {
        official_id: selectedOfficial
      });
      toast.success(t('officialsManagement.assignedSuccessfully'));
      setShowAssignDialog(false);
      setSelectedApplication(null);
      setSelectedOfficial("");
      setAssignFilterDepartment("");
      fetchOfficials();
      fetchUnassignedApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('officialsManagement.errorAssigning'));
    }
  };

  const openEditDialog = (official) => {
    setOfficialToEdit(official);
    setShowEditDialog(true);
  };
  
  const handleEditOfficialSave = async (editData) => {
    if (!officialToEdit) return;
    
    try {
      const updateData = {
        name: editData.name,
        phone: editData.phone,
        department: editData.department,
        designation: editData.designation
      };
      
      if (editData.password) {
        updateData.password = editData.password;
      }
      
      await api.put(`/officials/${officialToEdit.id}`, updateData);
      toast.success(t('officialsManagement.officialUpdated'));
      setShowEditDialog(false);
      setOfficialToEdit(null);
      // Refresh all officials lists including counts (in case designation changed)
      await refreshOfficials();
      fetchAssignableOfficials();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('officialsManagement.errorUpdating'));
    }
  };

  const openDeleteDialog = (official) => {
    setOfficialToDelete(official);
    setShowDeleteDialog(true);
  };

  const openAssignDialog = (application) => {
    setSelectedApplication(application);
    setAssignFilterDepartment("");
    setShowAssignDialog(true);
  };

  // Circle assignment handlers for Village Accountants
  const openCircleAssignDialog = (official) => {
    setOfficialForCircle(official);
    setCircleAssignHobli("");
    setCircleAssignCircle("");
    setShowCircleAssignDialog(true);
  };

  const handleAssignCircle = async () => {
    if (!officialForCircle || !circleAssignHobli || !circleAssignCircle) {
      toast.error(t('officialsManagement.selectHobliAndCircle') || "Please select both Hobli and Circle");
      return;
    }
    
    setCircleAssignLoading(true);
    try {
      await api.put(`/officials/${officialForCircle.id}/assign-circle`, {
        official_id: officialForCircle.id,
        hobli: circleAssignHobli,
        circle: circleAssignCircle
      });
      toast.success(t('officialsManagement.circleAdded') || "Circle added successfully");
      setShowCircleAssignDialog(false);
      setOfficialForCircle(null);
      setCircleAssignHobli("");
      setCircleAssignCircle("");
      fetchOfficials();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('officialsManagement.circleAssignFailed') || "Failed to add circle");
    } finally {
      setCircleAssignLoading(false);
    }
  };

  // Handle removing a specific circle assignment
  const openRemoveCircleDialog = (official, assignment) => {
    setOfficialForCircle(official);
    setCircleToRemove(assignment);
    setShowRemoveCircleDialog(true);
  };

  const handleRemoveCircle = async () => {
    if (!officialForCircle || !circleToRemove) return;
    
    setCircleAssignLoading(true);
    try {
      await api.delete(`/officials/${officialForCircle.id}/remove-circle?hobli=${encodeURIComponent(circleToRemove.hobli)}&circle=${encodeURIComponent(circleToRemove.circle)}`);
      toast.success(t('officialsManagement.circleRemoved') || "Circle removed successfully");
      setShowRemoveCircleDialog(false);
      setOfficialForCircle(null);
      setCircleToRemove(null);
      fetchOfficials();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('officialsManagement.circleRemoveFailed') || "Failed to remove circle");
    } finally {
      setCircleAssignLoading(false);
    }
  };

  // Get available circles for the selected hobli in circle assignment dialog
  const circleAssignAvailableCircles = useMemo(() => {
    if (!circleAssignHobli) return [];
    return CIRCLES_BY_HOBLI[circleAssignHobli] || [];
  }, [circleAssignHobli]);

  const getDesignationLabel = (designation) => {
    const found = ALL_DESIGNATIONS.find(d => d.value === designation);
    return found ? t(found.labelKey) : designation || "-";
  };

  const getDepartmentLabel = (department) => {
    const found = DEPARTMENTS.find(d => d.value === department);
    return found ? t(found.labelKey) : department || "-";
  };

  // Designation hierarchy order (lower number = higher rank)
  const DESIGNATION_ORDER = {
    // Revenue Department
    "tahsildar": 1,
    "shirestedar": 2,
    "deputy_tahsildar": 2,  // Same level as Shirestedar
    "revenue_inspector": 3,
    "village_accountant": 4,
    "first_division_clerk": 5,
    "second_division_clerk": 6,
    // Survey Department
    "ddlr": 1,
    "adlr": 2,
    "surveyor": 3,
    "survey_inspector": 4,
    "asst_surveyor": 5,
    // GOK Officials
    "minister": 1,
    "principal_secretary": 2,
    "gok_secretary": 3,
    "dc": 4,
    "ac": 5,
  };

  // Sort officials by designation hierarchy
  const sortByDesignation = (officials) => {
    return [...officials].sort((a, b) => {
      const orderA = DESIGNATION_ORDER[a.designation] || 99;
      const orderB = DESIGNATION_ORDER[b.designation] || 99;
      if (orderA !== orderB) return orderA - orderB;
      // If same rank, sort by name
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  // Group officials by department (including those without designation)
  const revenueOfficials = sortByDesignation(officials.filter(o => {
    if (o.department === "revenue") return true;
    if (o.designation && getDesignationDepartment(o.designation) === "revenue") return true;
    return false;
  }));
  
  const surveyOfficials = sortByDesignation(officials.filter(o => {
    if (o.department === "survey") return true;
    if (o.designation && getDesignationDepartment(o.designation) === "survey") return true;
    if (!o.department && !o.designation) return true; // Show unclassified in survey section
    return false;
  }));

  // Pagination logic - combine all officials for pagination
  const allFilteredOfficials = useMemo(() => {
    if (filterDepartment === "revenue") return revenueOfficials;
    if (filterDepartment === "survey") return surveyOfficials;
    return [...revenueOfficials, ...surveyOfficials];
  }, [filterDepartment, revenueOfficials, surveyOfficials]);

  const totalOfficials = allFilteredOfficials.length;
  const totalOfficialsPages = Math.ceil(totalOfficials / officialsPerPage);
  
  // Get paginated officials
  const paginatedOfficials = useMemo(() => {
    const startIndex = (officialsPage - 1) * officialsPerPage;
    const endIndex = startIndex + officialsPerPage;
    return allFilteredOfficials.slice(startIndex, endIndex);
  }, [allFilteredOfficials, officialsPage, officialsPerPage]);

  // Split paginated officials by department for display
  const paginatedRevenueOfficials = paginatedOfficials.filter(o => {
    if (o.department === "revenue") return true;
    if (o.designation && getDesignationDepartment(o.designation) === "revenue") return true;
    return false;
  });
  
  const paginatedSurveyOfficials = paginatedOfficials.filter(o => {
    if (o.department === "survey") return true;
    if (o.designation && getDesignationDepartment(o.designation) === "survey") return true;
    if (!o.department && !o.designation) return true;
    return false;
  });

  // Reset page when filters change
  useEffect(() => {
    setOfficialsPage(1);
  }, [filterDesignation, filterDepartment, filterHobli, filterCircle]);

  // Calculate designation counts from ALL officials (not filtered)
  const designationCounts = useMemo(() => {
    const counts = {};
    allOfficials.forEach(o => {
      const designation = o.designation || 'unknown';
      counts[designation] = (counts[designation] || 0) + 1;
    });
    return counts;
  }, [allOfficials]);

  // Designations allowed for assignment
  const TALUK_OFFICE_DESIGNATIONS = ["first_division_clerk", "second_division_clerk"];
  const FIELD_OFFICE_DESIGNATIONS = ["deputy_tahsildar", "revenue_inspector", "village_accountant"];
  const SURVEY_DESIGNATIONS_LIST = ["ddlr", "adlr", "surveyor"];

  // Hobli name normalization helper
  const normalizeHobliName = (hobli) => {
    if (!hobli) return '';
    const lower = hobli.toLowerCase();
    // Map both Kannada and English to a standard key
    const kannadaToKey = {
      "ಕಸಬಾ": "kasaba",
      "ಕುದೂರು": "kudur",
      "ಮಾಡಬಾಳು": "madabalu",
      "ತಿಪ್ಪಸಂದ್ರ": "tippasandra",
      "ಮಂಚನಬೆಲೆ": "manchanabele",
    };
    return kannadaToKey[hobli] || lower;
  };

  // Filter assignable officials for assignment dialog
  // Revenue: Submitted status → Taluk Office officials, Other status → Field Office officials (Hobli-based)
  // Survey: All Survey department officials
  const filteredOfficialsForAssign = useMemo(() => {
    if (!selectedApplication) return [];
    
    const applicationHobli = normalizeHobliName(selectedApplication.hobli);
    const applicationStatus = selectedApplication.status?.toLowerCase();
    const isSubmittedStatus = applicationStatus === "submitted";
    
    return assignableOfficials.filter(official => {
      const designation = official.designation?.toLowerCase();
      const officeLocation = official.office_location?.toLowerCase();
      const officialDept = official.department || getDesignationDepartment(official.designation);
      
      // Survey Department filter
      if (assignFilterDepartment === "survey") {
        return officialDept === "survey";
      }
      
      // Revenue Department filter (default)
      if (!assignFilterDepartment || assignFilterDepartment === "revenue") {
        // For "Submitted" status - show Taluk Office officials only
        if (isSubmittedStatus) {
          // Include FDC, SDC
          if (TALUK_OFFICE_DESIGNATIONS.includes(designation)) {
            return true;
          }
          // Include VAs assigned to Taluk Office
          if (designation === "village_accountant" && officeLocation === "taluk office") {
            return true;
          }
          return false;
        }
        
        // For other statuses - show Field Office officials from application's Hobli
        if (FIELD_OFFICE_DESIGNATIONS.includes(designation)) {
          // For VAs (not at Taluk Office), check circle_assignments hobli
          if (designation === "village_accountant") {
            // Exclude Taluk Office VAs
            if (officeLocation === "taluk office") {
              return false;
            }
            const circleAssignments = official.circle_assignments || [];
            return circleAssignments.some(ca => {
              const hobliValue = typeof ca === 'string' ? ca : (ca?.hobli || '');
              return normalizeHobliName(hobliValue) === applicationHobli;
            });
          }
          
          // For Deputy Tahsildar and RI, check assigned_hobli
          const officialHobli = normalizeHobliName(official.assigned_hobli);
          return officialHobli === applicationHobli;
        }
        
        return false;
      }
      
      return false;
    });
  }, [assignableOfficials, selectedApplication, assignFilterDepartment]);

  // Loading spinner component
  const LoadingSpinner = () => <LoadingOverlay fullPage={false} />;

  return (
    <div className="space-y-6" data-testid="officials-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-emerald-700 hidden md:block" />
          <div>
            <h2 className="text-base md:text-2xl font-manrope font-bold text-slate-900 tracking-tight">
              {t('officialsManagement.title')}
            </h2>
            <p className="text-xs md:text-sm text-slate-500 hidden md:block">
              {userDepartment === 'revenue' 
                ? t('officialsManagement.descriptionRevenue') 
                : userDepartment === 'survey' 
                  ? t('officialsManagement.descriptionSurvey')
                  : t('officialsManagement.description')}
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-3">
          {/* Import Officials Button - hidden on mobile */}
          <Button 
            variant="outline"
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 hidden md:flex" 
            data-testid="import-officials-btn"
            onClick={() => setShowImportDialog(true)}
          >
            <Upload className="w-4 h-4 mr-2" />
            {t('officialsManagement.importOfficials')}
          </Button>
          
          {/* Create Official Button */}
          <Button 
            className="bg-emerald-800 hover:bg-emerald-900 text-white" 
            data-testid="create-official-btn"
            onClick={() => setShowCreateDialog(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {t('officialsManagement.createOfficial')}
          </Button>
        </div>
      </div>

      {/* Import Officials Dialog */}
      <ImportOfficialsDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onImportComplete={refreshOfficials}
      />

      {/* Create Official Dialog - Separate Component for Performance */}
      <CreateOfficialDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        userDepartment={userDepartment}
        onSubmit={handleCreateOfficial}
      />

      {/* Edit Official Dialog - Separate Component for Performance */}
      <EditOfficialDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        official={officialToEdit}
        userDepartment={userDepartment}
        onSave={handleEditOfficialSave}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('officialsManagement.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('officialsManagement.deleteConfirmMessage', { name: officialToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('officialsManagement.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteOfficial}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-official"
            >
              {t('officialsManagement.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Application Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('officialsManagement.assignApplication')}</DialogTitle>
          </DialogHeader>
          {selectedApplication && (
            <div className="space-y-4">
              <Card className="p-3 bg-stone-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">#{selectedApplication.land_survey_number}</p>
                    <p className="text-sm text-slate-600">{selectedApplication.farmer_name}</p>
                    <p className="text-xs text-slate-500">{selectedApplication.location}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className={`text-xs ${
                      selectedApplication.status?.toLowerCase() === 'submitted' 
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-amber-50 text-amber-700 border-amber-200'
                    }`}>
                      {t(`status.${selectedApplication.status}`) || selectedApplication.status}
                    </Badge>
                  </div>
                </div>
                {selectedApplication.hobli && (
                  <Badge variant="outline" className="mt-2 bg-purple-50 text-purple-700 border-purple-200">
                    {t('officialsManagement.hobliLabel') || 'Hobli'}: {selectedApplication.hobli}
                  </Badge>
                )}
              </Card>
              
              {/* Department Filter - Only 2 options */}
              <div>
                <Label>{t('officialsManagement.selectDepartment')}</Label>
                <Select 
                  value={assignFilterDepartment || "revenue"} 
                  onValueChange={(val) => {
                    setAssignFilterDepartment(val);
                    setSelectedOfficial(""); // Reset selected official when filter changes
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="assign-dept-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">
                      {t('department.revenue')}
                    </SelectItem>
                    <SelectItem value="survey">
                      {t('department.survey')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Info about filtered officials */}
              <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                {assignFilterDepartment === "survey" 
                  ? (t('officialsManagement.showingSurveyOfficials') || 'Showing all Survey department officials')
                  : selectedApplication.status?.toLowerCase() === 'submitted'
                    ? (t('officialsManagement.showingTalukOffice') || 'Showing Taluk Office officials (FDC/SDC/VA)')
                    : (t('officialsManagement.showingFieldOffice') || `Showing Field Office officials for Hobli: ${selectedApplication.hobli}`)
                }
              </div>
              
              <div>
                <Label>{t('officialsManagement.selectOfficial')}</Label>
                <Select value={selectedOfficial} onValueChange={setSelectedOfficial}>
                  <SelectTrigger className="mt-1" data-testid="assign-official-select">
                    <SelectValue placeholder={t('officialsManagement.selectOfficial')} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredOfficialsForAssign.length === 0 ? (
                      <div className="p-2 text-sm text-slate-500 text-center">
                        {t('officialsManagement.noOfficialsAvailable') || 'No officials available'}
                      </div>
                    ) : (
                      filteredOfficialsForAssign.map((official) => (
                        <SelectItem key={official.id} value={official.id}>
                          <div className="flex items-center gap-2">
                            <span>{official.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {t(`designation.${official.designation}`) || official.designation}
                            </Badge>
                            <Badge variant="outline" className="text-xs bg-amber-50">
                              {official.workload?.pending || 0} {t('officialsManagement.pendingShort')}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                  {t('officialsManagement.cancel')}
                </Button>
                <Button 
                  onClick={handleAssignApplication}
                  disabled={!selectedOfficial}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white"
                  data-testid="confirm-assign"
                >
                  {t('officialsManagement.assign')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="officials" className="space-y-4">
        <TabsContent value="officials" className="space-y-4 mt-0">
          {/* Filter */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500" />
              <Select 
                value={filterDepartment || "all"} 
                onValueChange={(val) => {
                  if (!isDepartmentLocked) {
                    setFilterDepartment(val === "all" ? "" : val);
                    setFilterDesignation(""); // Reset designation filter when department changes
                  }
                }}
                disabled={isDepartmentLocked}
              >
                <SelectTrigger 
                  className={`w-[200px] ${isDepartmentLocked ? 'bg-slate-100 cursor-not-allowed' : ''}`} 
                  data-testid="filter-department"
                >
                  <SelectValue placeholder={t('department.filterByDepartment')} />
                </SelectTrigger>
                <SelectContent>
                  {!isDepartmentLocked && (
                    <SelectItem value="all">{t('department.allDepartments')}</SelectItem>
                  )}
                  {DEPARTMENTS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {t(d.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <Select 
                value={filterDesignation || "all"} 
                onValueChange={(val) => {
                  const newDesignation = val === "all" ? "" : val;
                  setFilterDesignation(newDesignation);
                  // Reset hobli/circle filters if not selecting Village Accountant
                  if (newDesignation !== "village_accountant") {
                    setFilterHobli("all");
                    setFilterCircle("all");
                  }
                }}
              >
                <SelectTrigger className="w-[200px]" data-testid="filter-designation">
                  <SelectValue placeholder={t('officialsManagement.filterByDesignation')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('officialsManagement.allDesignations')}</SelectItem>
                  {/* Show designations based on user's authority */}
                  {getFilterDesignations().map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {t(d.labelKey)} ({designationCounts[d.value] || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Hobli Filter - only show when Village Accountant designation is selected */}
            {filterDesignation === "village_accountant" && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-amber-500" />
                <Select 
                  value={filterHobli} 
                  onValueChange={(val) => {
                    setFilterHobli(val);
                    setFilterCircle("all"); // Reset circle when hobli changes
                  }}
                >
                  <SelectTrigger className="w-[180px]" data-testid="filter-hobli">
                    <SelectValue placeholder={t('officialsManagement.filterByHobli')} />
                  </SelectTrigger>
                  <SelectContent>
                    {HOBLIS.map((h) => (
                      <SelectItem key={h.value} value={h.value}>
                        {t(h.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Circle Filter - only show when Village Accountant is selected AND a Hobli is selected */}
            {filterDesignation === "village_accountant" && filterHobli !== "all" && filterAvailableCircles.length > 0 && (
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-amber-500" />
                <Select value={filterCircle} onValueChange={setFilterCircle}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-circle">
                    <SelectValue placeholder={t('officialsManagement.filterByCircle')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('circle.allCircles')}</SelectItem>
                    {filterAvailableCircles.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {getCircleLabel(c, currentLanguage)} ({c.villageCount ?? 0} {t('officialsManagement.villages')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Clear location filters button - only show when VA is selected and filters are active */}
            {filterDesignation === "village_accountant" && (filterHobli !== "all" || filterCircle !== "all") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilterHobli("all");
                  setFilterCircle("all");
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                Clear Filters
              </Button>
            )}
          </div>
          
          {/* Active filter indicator - only show when VA is selected and filters are active */}
          {filterDesignation === "village_accountant" && (filterHobli !== "all" || filterCircle !== "all") && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">{t('officialsManagement.filteringBy')}:</span>
              {filterHobli !== "all" && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  {t('officialsManagement.hobliLabel')}: {filterHobli}
                </Badge>
              )}
              {filterCircle !== "all" && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  {t('officialsManagement.circleLabel')}: {getCircleLabel(filterAvailableCircles.find(c => c.value === filterCircle) || {label: filterCircle}, currentLanguage)}
                </Badge>
              )}
            </div>
          )}
          
          {/* VA Summary Card - Show count of Village Accountants per Hobli when VA filter is active */}
          {filterDesignation === "village_accountant" && filterHobli === "all" && (
            <VAHobliSummaryCard officials={officials} t={t} onHobliClick={setFilterHobli} />
          )}

          {/* Top Pagination Bar - Info and Controls on same line */}
          {!loading && totalOfficials > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-sm text-slate-600 bg-slate-50 px-3 md:px-4 py-2 rounded-lg">
              <span className="whitespace-nowrap text-xs md:text-sm">
                {t('officialsManagement.showingOfficials', {
                  start: ((officialsPage - 1) * officialsPerPage) + 1,
                  end: Math.min(officialsPage * officialsPerPage, totalOfficials),
                  total: totalOfficials
                }) || `Showing ${((officialsPage - 1) * officialsPerPage) + 1} - ${Math.min(officialsPage * officialsPerPage, totalOfficials)} of ${totalOfficials} officials`}
              </span>
              
              {/* Pagination Controls - Center */}
              {totalOfficials > officialsPerPage && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOfficialsPage(1)}
                    disabled={officialsPage === 1}
                    className="px-1.5 md:px-2 h-7 md:h-8"
                    data-testid="officials-first-page-top"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    <ChevronLeft className="w-3.5 h-3.5 -ml-2" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOfficialsPage(p => Math.max(1, p - 1))}
                    disabled={officialsPage === 1}
                    className="px-1.5 md:px-2 h-7 md:h-8"
                    data-testid="officials-prev-page-top"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  
                  {/* Page numbers - show fewer on mobile */}
                  <div className="flex items-center gap-0.5 md:gap-1">
                    {Array.from({ length: Math.min(window.innerWidth < 768 ? 3 : 5, totalOfficialsPages) }, (_, i) => {
                      let pageNum;
                      const maxVisible = window.innerWidth < 768 ? 3 : 5;
                      if (totalOfficialsPages <= maxVisible) {
                        pageNum = i + 1;
                      } else if (officialsPage <= Math.ceil(maxVisible / 2)) {
                        pageNum = i + 1;
                      } else if (officialsPage >= totalOfficialsPages - Math.floor(maxVisible / 2)) {
                        pageNum = totalOfficialsPages - maxVisible + 1 + i;
                      } else {
                        pageNum = officialsPage - Math.floor(maxVisible / 2) + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={officialsPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setOfficialsPage(pageNum)}
                          className={`w-7 h-7 md:w-8 md:h-8 text-xs ${officialsPage === pageNum ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                          data-testid={`officials-page-${pageNum}-top`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOfficialsPage(p => Math.min(totalOfficialsPages, p + 1))}
                    disabled={officialsPage === totalOfficialsPages}
                    className="px-1.5 md:px-2 h-7 md:h-8"
                    data-testid="officials-next-page-top"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOfficialsPage(totalOfficialsPages)}
                    disabled={officialsPage === totalOfficialsPages}
                    className="px-1.5 md:px-2 h-7 md:h-8"
                    data-testid="officials-last-page-top"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                    <ChevronRight className="w-3.5 h-3.5 -ml-2" />
                  </Button>
                </div>
              )}
              
              <span className="text-slate-500 whitespace-nowrap text-xs md:text-sm hidden md:inline">
                {t('officialsManagement.pageInfo', { current: officialsPage, total: totalOfficialsPages }) || `Page ${officialsPage} of ${totalOfficialsPages}`}
              </span>
            </div>
          )}

          {/* Loading Spinner */}
          {loading && <LoadingSpinner />}

          {/* Revenue Department Officials */}
          {!loading && (filterDepartment === "" || filterDepartment === "revenue") && paginatedRevenueOfficials.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-emerald-200"></div>
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                  <Building2 className="w-3 h-3 mr-1" />
                  {t('department.revenue')} ({revenueOfficials.length})
                </Badge>
                <div className="h-px flex-1 bg-emerald-200"></div>
              </div>
              <OfficialsTable
                officials={paginatedRevenueOfficials}
                onEdit={openEditDialog}
                onToggleStatus={handleToggleStatus}
                onDelete={openDeleteDialog}
                onAssignCircle={openCircleAssignDialog}
                onRemoveCircle={openRemoveCircleDialog}
                userDesignation={userDesignation}
                department="revenue"
              />
            </div>
          )}

          {/* Survey Department Officials */}
          {!loading && (filterDepartment === "" || filterDepartment === "survey") && paginatedSurveyOfficials.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-blue-200"></div>
                <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                  <Building2 className="w-3 h-3 mr-1" />
                  {t('department.survey')} ({surveyOfficials.length})
                </Badge>
                <div className="h-px flex-1 bg-blue-200"></div>
              </div>
              <OfficialsTable
                officials={paginatedSurveyOfficials}
                onEdit={openEditDialog}
                onToggleStatus={handleToggleStatus}
                onDelete={openDeleteDialog}
                onAssignCircle={openCircleAssignDialog}
                onRemoveCircle={openRemoveCircleDialog}
                userDesignation={userDesignation}
                department="survey"
              />
            </div>
          )}

          {/* Bottom Pagination Bar */}
          {!loading && totalOfficials > officialsPerPage && (
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-sm text-slate-600 bg-slate-50 px-3 md:px-4 py-2 rounded-lg mt-4">
              <span className="whitespace-nowrap text-xs md:text-sm">
                {t('officialsManagement.showingOfficials', {
                  start: ((officialsPage - 1) * officialsPerPage) + 1,
                  end: Math.min(officialsPage * officialsPerPage, totalOfficials),
                  total: totalOfficials
                }) || `Showing ${((officialsPage - 1) * officialsPerPage) + 1} - ${Math.min(officialsPage * officialsPerPage, totalOfficials)} of ${totalOfficials} officials`}
              </span>
              
              {/* Pagination Controls - Center */}
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOfficialsPage(1)}
                  disabled={officialsPage === 1}
                  className="px-1.5 md:px-2 h-7 md:h-8"
                  data-testid="officials-first-page"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <ChevronLeft className="w-3.5 h-3.5 -ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOfficialsPage(p => Math.max(1, p - 1))}
                  disabled={officialsPage === 1}
                  className="px-1.5 md:px-2 h-7 md:h-8"
                  data-testid="officials-prev-page"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                
                {/* Page numbers - fewer on mobile */}
                <div className="flex items-center gap-0.5 md:gap-1">
                  {Array.from({ length: Math.min(window.innerWidth < 768 ? 3 : 5, totalOfficialsPages) }, (_, i) => {
                    let pageNum;
                    const maxVisible = window.innerWidth < 768 ? 3 : 5;
                    if (totalOfficialsPages <= maxVisible) {
                      pageNum = i + 1;
                    } else if (officialsPage <= Math.ceil(maxVisible / 2)) {
                      pageNum = i + 1;
                    } else if (officialsPage >= totalOfficialsPages - Math.floor(maxVisible / 2)) {
                      pageNum = totalOfficialsPages - maxVisible + 1 + i;
                    } else {
                      pageNum = officialsPage - Math.floor(maxVisible / 2) + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={officialsPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setOfficialsPage(pageNum)}
                        className={`w-7 h-7 md:w-8 md:h-8 text-xs ${officialsPage === pageNum ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                        data-testid={`officials-page-${pageNum}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOfficialsPage(p => Math.min(totalOfficialsPages, p + 1))}
                  disabled={officialsPage === totalOfficialsPages}
                  className="px-1.5 md:px-2 h-7 md:h-8"
                  data-testid="officials-next-page"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOfficialsPage(totalOfficialsPages)}
                  disabled={officialsPage === totalOfficialsPages}
                  className="px-1.5 md:px-2 h-7 md:h-8"
                  data-testid="officials-last-page"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                  <ChevronRight className="w-3.5 h-3.5 -ml-2" />
                </Button>
              </div>
              
              <span className="text-slate-500 whitespace-nowrap text-xs md:text-sm hidden md:inline">
                {t('officialsManagement.pageInfo', { current: officialsPage, total: totalOfficialsPages }) || `Page ${officialsPage} of ${totalOfficialsPages}`}
              </span>
            </div>
          )}

          {!loading && officials.length === 0 && (
            <Card className="p-8 text-center bg-stone-50">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{t('officialsManagement.noOfficials')}</p>
            </Card>
          )}
        </TabsContent>

        {/* Circle Assignment Dialog for Village Accountants */}
        <Dialog open={showCircleAssignDialog} onOpenChange={setShowCircleAssignDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-amber-600" />
                  {t('officialsManagement.addCircleTitle') || 'Add Circle Assignment'}
                </div>
              </DialogTitle>
              <DialogDescription>
                {officialForCircle?.name && (
                  <span className="font-medium text-slate-700">{officialForCircle.name}</span>
                )}
                {' - '}
                {t('officialsManagement.addCircleDescription') || 'Add a new circle to this VA\'s responsibilities.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Current Assignments Display */}
              {officialForCircle && (() => {
                const assignments = officialForCircle.circle_assignments || [];
                const legacyAssignment = !assignments.length && officialForCircle.assigned_circle ? 
                  [{ hobli: officialForCircle.assigned_hobli, circle: officialForCircle.assigned_circle }] : [];
                const allAssignments = assignments.length ? assignments : legacyAssignment;
                
                return allAssignments.length > 0 && (
                  <div className="bg-stone-50 border border-stone-200 rounded-md p-3">
                    <p className="text-xs font-medium text-stone-600 mb-2">
                      {t('officialsManagement.currentAssignments') || 'Current Assignments'} ({allAssignments.length}):
                    </p>
                    <div className="space-y-1">
                      {allAssignments.map((a, i) => {
                        const circleValue = typeof a === 'string' ? a : (a?.circle || '');
                        const hobliValue = typeof a === 'string' ? '' : (a?.hobli || '');
                        return (
                          <div key={i} className="text-sm text-stone-700">
                            • {circleValue.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} 
                            {hobliValue && <span className="text-stone-500 ml-1">({hobliValue})</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              
              {/* Hobli Selection */}
              <div className="space-y-2">
                <Label>{t('officialsManagement.hobliLabel') || 'Hobli'}</Label>
                <Select 
                  value={circleAssignHobli} 
                  onValueChange={(val) => {
                    setCircleAssignHobli(val);
                    setCircleAssignCircle(""); // Reset circle when hobli changes
                  }}
                >
                  <SelectTrigger data-testid="circle-assign-hobli-select">
                    <SelectValue placeholder={t('officialsManagement.selectHobli') || 'Select Hobli'} />
                  </SelectTrigger>
                  <SelectContent>
                    {HOBLIS.filter(h => h.value !== "all").map((hobli) => (
                      <SelectItem key={hobli.value} value={hobli.value}>
                        {t(hobli.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Circle Selection */}
              <div className="space-y-2">
                <Label>{t('officialsManagement.circleLabel') || 'Circle'}</Label>
                <Select 
                  value={circleAssignCircle} 
                  onValueChange={setCircleAssignCircle}
                  disabled={!circleAssignHobli}
                >
                  <SelectTrigger data-testid="circle-assign-circle-select">
                    <SelectValue placeholder={
                      circleAssignHobli 
                        ? (t('officialsManagement.selectCircle') || 'Select Circle')
                        : (t('officialsManagement.selectHobliFirst') || 'Select Hobli first')
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {circleAssignAvailableCircles.map((circle) => (
                      <SelectItem key={circle.value} value={circle.value}>
                        {getCircleLabel(circle, currentLanguage)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCircleAssignDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleAssignCircle} 
                disabled={!circleAssignHobli || !circleAssignCircle || circleAssignLoading}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-circle-assign-btn"
              >
                {circleAssignLoading 
                  ? (t('common.processing') || 'Processing...') 
                  : (t('officialsManagement.addCircle') || 'Add Circle')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove Circle Confirmation Dialog */}
        <AlertDialog open={showRemoveCircleDialog} onOpenChange={setShowRemoveCircleDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('officialsManagement.removeCircleTitle') || 'Remove Circle Assignment'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('officialsManagement.removeCircleConfirm', { 
                  circle: circleToRemove?.circle?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
                  hobli: circleToRemove?.hobli,
                  name: officialForCircle?.name 
                }) || `Are you sure you want to remove "${circleToRemove?.circle?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}" (${circleToRemove?.hobli}) from ${officialForCircle?.name}?`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setShowRemoveCircleDialog(false);
                setCircleToRemove(null);
                setOfficialForCircle(null);
              }}>
                {t('common.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleRemoveCircle}
                disabled={circleAssignLoading}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-remove-circle-btn"
              >
                {circleAssignLoading ? t('common.processing') : (t('officialsManagement.removeCircle') || 'Remove')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Tabs>
    </div>
  );
}
